import { useCallback, useState, useEffect } from 'react';

import { InputProps } from '@components/Input/InputProps';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { isNumeric } from '@utils/characters';

import { Container, StyledCheckbox, InputField } from './styled';

const useTCKN = (
  value?: string,
  isTurkishCitizen?: boolean,
  onToggle?: (value: boolean) => void,
  isTcknRequired?: boolean
) => {
  const [hasIdentityNumber, setHasIdentityNumber] = useState(isTurkishCitizen);

  const [identityNumber, setIdentityNumber] = useState(
    (value ?? '').slice(0, 11)
  );

  const onChange = useCallback(
    (newValue: string) => {
      const sanitizedValue = hasIdentityNumber
        ? newValue.slice(0, 11)
        : newValue;
      setIdentityNumber(sanitizedValue);
    },
    [hasIdentityNumber]
  );

  const onHasIdentityNumberStateChange = useCallback(
    (x) => {
      setHasIdentityNumber(x);
      onToggle?.(x);
    },
    [onToggle]
  );

  useEffect(() => {
    if (value?.length >= 11) {
      setIdentityNumber(value ?? '');
    } else if (value?.length == 0) {
      setIdentityNumber('');
    }
  }, [value]);

  useEffect(() => {
    if (!isTurkishCitizen) setIdentityNumber('');
  }, [isTurkishCitizen]);

  useEffect(() => {
    if (isTcknRequired) setIdentityNumber('');
  }, [hasIdentityNumber]);

  useEffect(() => {
    setHasIdentityNumber(isTurkishCitizen);
  }, [isTurkishCitizen]);

  return {
    mode: hasIdentityNumber ? ('active' as const) : ('inactive' as const),
    onChange,
    hasIdentityNumber,
    identityNumber,
    onHasIdentityNumberStateChange,
  };
};

export type TCKNProps = {
  onToggle?: (state: boolean) => void;
  isTurkishCitizen?: boolean;
  showInput?: boolean;
  hideCheckbox?: boolean;
  onChange?: (value: string) => void;
  hasError?: boolean;
};

const TCKNInput = ({
  width,
  value,
  icon,
  suffix,
  onToggle,
  useNative,
  isTurkishCitizen,
  showInput = true,
  hideCheckbox,
  onChange: parentOnChange,
  alwayseditable,
  isTcknRequired,
  checkboxMode,
  tcknType,
  hasError,
  isTcknControl,
  ...rest
}: InputProps &
  TCKNProps & {
    alwayseditable: boolean;
    isTcknRequired?: boolean;
    isTcknControl?: boolean;
    checkboxMode?: 'controlled' | 'uncontrolled';
    tcknType?: string;
  }) => {
  const {
    mode,
    hasIdentityNumber,
    identityNumber,
    onChange,
    onHasIdentityNumberStateChange,
  } = useTCKN(value as string, isTurkishCitizen, onToggle, isTcknRequired);

  useSkipEffectOnMount(() => {
    parentOnChange?.(hasIdentityNumber || alwayseditable ? identityNumber : '');
  }, [identityNumber, hasIdentityNumber, alwayseditable]);

  return (
    <Container
      width={width}
      icon={icon}
      suffix={suffix}
      inputShown={showInput}
      disabled={rest.disabled}
      hasError={hasError}
    >
      {showInput ? (
        <InputField
          width='100%'
          mode={alwayseditable ? 'active' : mode}
          inputDisable={!isTcknControl}
          disabled={!isTcknControl}
          type={tcknType === 'tckn' ? tcknType : 'number'}
          value={identityNumber}
          onChange={onChange}
          useNative={useNative}
          onKeyDown={(e) => {
            if (!isNumeric(e.key)) e.preventDefault();
          }}
          onWheel={(e) => {
            (e.target as HTMLElement).blur();
          }}
          {...rest}
          inputMode='numeric'
        />
      ) : null}
      {hideCheckbox ? null : (
        <StyledCheckbox
          variant='outline'
          title='TC Vatandaşıyım'
          onChange={rest.disabled ? undefined : onHasIdentityNumberStateChange}
          checked={hasIdentityNumber}
          inputShown={showInput}
          disabled={rest.disabled}
          mode={checkboxMode}
        />
      )}
    </Container>
  );
};

export default TCKNInput;
